/**
 * @fileoverview Auto-session timeout management utilities.
 * Manages session timeout configuration, tracking, and automatic logout based on user activity or login time.
 */

import _ from "lodash";
import moment from "moment";
import LocalStorageManager from "@/localStorageManager";
import store from "@/store";
import { logout } from "@/helperMethods/auth";

/**
 * Clears all auto-session related data from local storage.
 *
 * @example
 * resetAutoSessionLocalStorage();
 */
export function resetAutoSessionLocalStorage() {
  LocalStorageManager.removeItem("startAutoSession");
  LocalStorageManager.removeItem("autoSessionTimeout");
  LocalStorageManager.removeItem("sessionTimeoutDuration");
  LocalStorageManager.removeItem("loginTime");
  LocalStorageManager.removeItem("numberOfTabs");
  LocalStorageManager.removeItem("idleTabs");
}

/**
 * Initializes auto-session tracking in local storage.
 * Sets start time and session configuration from store.
 *
 * @example
 * setAutoSessionLocalStorage();
 */
export function setAutoSessionLocalStorage() {
  setStartAutoSession();
  setAutoSessionConfig();
}

/**
 * Converts time value to minutes based on unit.
 *
 * @param {number} value - Time value
 * @param {string} unit - Time unit (days, hours, minutes)
 * @returns {number} Time in minutes
 *
 * @example
 * toMinutes(2, 'hours'); // 120
 */
export function toMinutes(value, unit) {
  switch (unit) {
    case "days":
      return value * 24 * 60;
    case "hours":
      return value * 60;
    default:
      return value;
  }
}

/**
 * Loads session timeout configuration from store and saves to local storage.
 * Includes enabled status, duration, and timeout type (lastActivity or loginTime).
 *
 * @example
 * setAutoSessionConfig();
 */
export function setAutoSessionConfig() {
  const sessionTimeoutEnabled = _.get(
    store,
    "state.modules.system.sessionTimeoutConfig.enabled",
    false
  );
  LocalStorageManager.setItem("autoSessionTimeout", sessionTimeoutEnabled);

  const sessionTimeoutDuration = _.get(
    store,
    "state.modules.system.sessionTimeoutConfig.duration.time",
    15
  );
  const sessionTimeoutDurationUnit = _.get(
    store,
    "state.modules.system.sessionTimeoutConfig.duration.unit",
    "minutes"
  );
  LocalStorageManager.setItem(
    "sessionTimeoutDuration",
    toMinutes(sessionTimeoutDuration, sessionTimeoutDurationUnit)
  );

  const sessionTimeoutType = _.get(
    store,
    "state.modules.system.sessionTimeoutConfig.type",
    "lastActivity"
  );
  LocalStorageManager.setItem("sessionTimeoutType", sessionTimeoutType);
}

/**
 * Records the current timestamp as session start time.
 * Used for lastActivity-based timeout tracking.
 *
 * @example
 * setStartAutoSession();
 */
export function setStartAutoSession() {
  const startAutoSession = Date.now();
  LocalStorageManager.setItem("startAutoSession", startAutoSession);
}

/**
 * Records the current timestamp as login time.
 * Used for loginTime-based timeout tracking.
 *
 * @example
 * setLoginTime();
 */
export function setLoginTime() {
  const loginTime = Date.now();
  LocalStorageManager.setItem("loginTime", loginTime);
}

/**
 * Checks if the current session has expired based on configured timeout.
 * Compares current time against either lastActivity or loginTime based on configuration.
 *
 * @returns {boolean | undefined} True if session expired, undefined if not configured
 *
 * @example
 * if (checkAutoSessionLocalStorage()) {
 *   onInactiveSession();
 * }
 */
export function checkAutoSessionLocalStorage() {
  const sessionTimeoutEnabled = LocalStorageManager.getItem("autoSessionTimeout");
  if (sessionTimeoutEnabled !== "true") {
    return;
  }

  const sessionTimeoutType = LocalStorageManager.getItem("sessionTimeoutType");
  let startAutoSession;
  if (sessionTimeoutType === "lastActivity") {
    startAutoSession = parseInt(LocalStorageManager.getItem("startAutoSession") as string);
  } else if (sessionTimeoutType === "loginTime") {
    startAutoSession = parseInt(LocalStorageManager.getItem("loginTime") as string);
  } else {
    return;
  }

  // checking startAutoSession exists and a number type to continue
  if (isNaN(startAutoSession)) {
    return;
  }
  const start = moment(startAutoSession);

  let sessionTimeoutDuration = parseInt(
    LocalStorageManager.getItem("sessionTimeoutDuration") as string
  );
  if (isNaN(sessionTimeoutDuration)) {
    sessionTimeoutDuration = 15;
  }
  const expired = start.add(sessionTimeoutDuration, "minutes");
  const now = moment(Date.now());
  const sessionIsExpired = now.isAfter(expired);

  return sessionIsExpired;
}

/**
 * Handles session expiration by clearing storage and logging out.
 *
 * @example
 * onInactiveSession();
 */
export function onInactiveSession() {
  resetAutoSessionLocalStorage();
  logout();
}

/**
 * Increments the tab counter in local storage.
 * Used to track number of open dashboard tabs.
 *
 * @param {string} name - Local storage key name
 *
 * @example
 * addTab('numberOfTabs');
 */
export function addTab(name) {
  let noOfTabs = parseInt(LocalStorageManager.getItem(name) as string);
  if (isNaN(noOfTabs)) {
    noOfTabs = 1;
  } else {
    noOfTabs++;
  }
  LocalStorageManager.setItem(name, noOfTabs);
}

/**
 * Decrements the tab counter in local storage.
 * Used when a dashboard tab is closed.
 *
 * @param {string} name - Local storage key name
 *
 * @example
 * removeTab('numberOfTabs');
 */
export function removeTab(name) {
  let noOfTabs = parseInt(LocalStorageManager.getItem(name) as string);
  if (isNaN(noOfTabs)) {
    noOfTabs = 0;
  } else {
    noOfTabs--;
    if (noOfTabs < 0) {
      noOfTabs = 0;
    }
  }
  LocalStorageManager.setItem(name, noOfTabs);
}
